import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/pages.css";
import Banner from "../ui/Banner";
import products from "../../assets/images/products.png";
import Pagination from "../ui/Pagination";
import SearchBar from "../ui/SearchBar";
import { getListProduct } from "../../api";

// 공통 ProductAreaContent 컴포넌트
const ProductAreaContent = ({ title, productList, category }) => {
  const navigate = useNavigate();
  // Pagination 시작
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const productsPerPage = 6;
  // Pagination 끝
  // 검색바
  const [originalProducts, setOriginalProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setOriginalProducts(productList);
    setFilteredProducts(productList);
  }, [productList]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(originalProducts);
    } else {
      const filtered = originalProducts.filter((product) => product.title.toLowerCase().includes(searchTerm.toLowerCase()) || product.content.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredProducts(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, originalProducts]);

  useEffect(() => {
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    setDisplayedProducts(filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct));
  }, [currentPage, filteredProducts, productsPerPage]);

  const handleProductClick = (category, id, e) => {
    if (e) e.stopPropagation();
    navigate(`/products/${category}/${id}/`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: document.querySelector(".productarea-header").offsetTop - 100,
      behavior: "smooth",
    });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="productarea-section">
      <div className="productarea-header">
        <h2>{title}</h2>
        <p>현장에서 검증된 기술, 에코텍글로벌의 제품을 소개합니다.</p>
      </div>
      <div className="productarea-info">
        <p>총 {filteredProducts.length}건</p>
        <SearchBar onSearch={handleSearch} placeholder="검색어를 입력하세요." />
      </div>
      <hr />
      {displayedProducts.length > 0 ? (
        <div className="product-grid">
          {displayedProducts.map((product) => (
            <div
              key={product.id}
              className="product-box"
              onClick={(e) => handleProductClick(category, product.id, e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleProductClick(category, product.id, e);
                }
              }}
            >
              <div className="product-thumnail-container">
                <img src={`${product.thumbnail.file.url}`} alt={product.name} className="product-thumnail" />
                <div className="hover-overlay">
                  <span className="detail-text">자세히 보기</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 58 58" className="arrownew-icon">
                    <g data-name="그룹 62">
                      <g data-name="그룹 61">
                        <path
                          data-name="패스 44"
                          d="M9118.341 1662.063h23.6"
                          transform="translate(-9101.257 -1633.028)"
                          style={{ strokeLinecap: "round", stroke: "#fff", strokeWidth: "2px", fill: "none" }}
                        />
                        <path
                          data-name="패스 45"
                          d="m9187.706 1640.815 5.992 5.993-5.992 5.992"
                          transform="translate(-9153.012 -1617.773)"
                          style={{ strokeLinejoin: "round", strokeLinecap: "round", stroke: "#fff", strokeWidth: "2px", fill: "none" }}
                        />
                      </g>
                      <g data-name="타원 1" style={{ stroke: "#fff", strokeWidth: "2px", fill: "none" }}>
                        <circle cx="29" cy="29" r="29" style={{ stroke: "none" }} />
                        <circle cx="29" cy="29" r="28" style={{ fill: "none" }} />
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
              <div className="product-info">
                <h3>
                  {product.title}
                  {product.subtitle && (
                    <>
                      <br />
                      {product.subtitle}
                    </>
                  )}
                </h3>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p>검색 결과가 없습니다.</p>
        </div>
      )}
      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}
    </div>
  );
};

const Products = ({ category, siblings }) => {
  const navigate = useNavigate();
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeTab = category.en_name.toLowerCase();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getListProduct({ category: activeTab });
        setProductList(data.data.results ?? data.data);
        setLoading(false);
      } catch (err) {
        setError("제품 정보를 불러오는데 실패했습니다.");
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeTab]);

  const handleTabChange = (tabId) => {
    if (tabId === "construction") {
      navigate("/portfolio");
      return;
    }
    navigate(`/products/${tabId}`);
  };

  const bizMeta = {
    1: { title: "농산업사업부", description: ["농가의 현실에 꼭 맞는 솔루션", "검증된 기술력으로 완성된 에코텍글로벌의 제품을 소개합니다."] },
    2: { title: "첨단소재·부품사업부", description: ["차세대 소재와 핵심 부품 기술", "다양한 산업 분야에 혁신적인 솔루션을 제공합니다."] },
    3: { title: "K-방산사업부", description: ["첨단 기술과 혁신적인 솔루션을 바탕으로", "대한민국 방위산업의 미래를 만들어갑니다."] },
  };
  const currentBizMeta = bizMeta[category.order] || bizMeta[1];

  const bannerConfig = {
    backgroundImage: products,
    title: currentBizMeta.title,
    description: currentBizMeta.description,
    tabs: siblings.map((cat) => ({
      id: cat.en_name.toLowerCase(),
      name: cat.name,
    })),
  };

  return (
    <div className="product-container">
      <Banner {...bannerConfig} activeTab={activeTab} onTabChange={handleTabChange} />
      {loading && <div className="loading">제품 정보를 불러오는 중...</div>}
      {error && <div className="error">{error}</div>}
      {!loading && !error && (
        <ProductAreaContent title={category.name} productList={productList} category={activeTab} />
      )}
    </div>
  );
};

export default Products;
